// Layer 3 (transformation) — the one-throughput-axis figure of the locked comp
// (`.impeccable/mocks/decision/capacity.html`, variant A tab 1): the bottleneck
// ladder as a staircase (rate across, EBITDA up), the five circuit ceilings as
// lanes on the same stph scale, and the recovery curve underneath. Pure
// geometry from the engine read; the component only draws what this returns.

import type { CapacityRead } from '@/versions/v1/schema/capacity';
import type { BottleneckLadderProjection } from './BottleneckLadder.transform';
import type { SelectableScenarioId } from './ThroughputTab.transform';
import { formatCurrency } from './Scene.transform';

export type AxisTone = 'ink' | 'ink2' | 'ink3' | 'ok' | 'warn' | 'bad' | 'info' | 'accent' | 'surface';

export type AxisSpan = { text: string; tone: AxisTone; weight?: 400 | 500 | 600; size?: number };

export type AxisText = {
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
  size: number;
  weight: 400 | 500 | 600;
  tone: AxisTone;
  spans: readonly AxisSpan[];
  caps?: boolean;
};

export type AxisLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tone: AxisTone;
  width: number;
  dash?: string;
  opacity?: number;
};

export type AxisRect = { x: number; y: number; width: number; height: number; tone: AxisTone; opacity: number };

export type AxisCircle = {
  cx: number;
  cy: number;
  r: number;
  fill: AxisTone;
  stroke: AxisTone;
  strokeWidth: number;
  /** Marker title: what the point is, with its units. */
  title: string;
};

export type AxisPath = { d: string; tone: AxisTone; width: number };

export type CapacityAxisView = {
  width: number;
  height: number;
  ariaLabel: string;
  texts: readonly AxisText[];
  lines: readonly AxisLine[];
  rects: readonly AxisRect[];
  circles: readonly AxisCircle[];
  paths: readonly AxisPath[];
  /** Each staircase label's two-line box and each drawn staircase stroke (rung lines, risers, drop lines), in figure px; no box meets a stroke. */
  stepLabelBoxes: readonly AxisBox[];
  stairStrokes: readonly AxisBox[];
};

export type AxisBox = { left: number; right: number; top: number; bottom: number };

/** Left gutter for band and lane labels, right margin, both in px. */
const GUTTER = 150;
const RIGHT = 18;
const LANE_HEIGHT = 22;
const RECOVERY_HEIGHT = 150;
/** Approximate Inter advance widths (px per character) at the figure's sizes. */
const CHAR_12_BOLD = 6.9;
const CHAR_10_CAPS = 7.0;
const CHAR_10_5 = 5.9;

type Box = AxisBox;
/** Half-width of the clearance kept around every staircase stroke, in px. */
const STROKE_CLEARANCE = 3;

function oneDecimal(value: number): string {
  return (Math.round((value + 1e-9) * 10) / 10).toFixed(1);
}

function signedA(value: number): string {
  return `${value < 0 ? '−' : '+'}${formatCurrency(Math.abs(value))}`;
}

function overlaps(a: Box, b: Box): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

type Step = {
  kind: 'here' | 'lim' | 'op' | 'cap';
  x: number;
  e: number;
  title: string;
  pill: string | null;
  prefix: string;
  money: string;
  moneyTone: AxisTone;
  tooltip: string;
};

/**
 * The figure at a given pixel width. Text sits at true pixel size (the
 * component measures its width), so no label shrinks below the type floor.
 */
export function capacityAxisView(
  read: CapacityRead,
  ladder: BottleneckLadderProjection,
  selectedScenarioId: SelectableScenarioId,
  width: number,
): CapacityAxisView {
  const W = Math.max(560, width);
  const feedLimited = ladder.feedLimited;
  const current = read.scenarios.find((row) => row.id === 'current')!;
  const optimised = read.scenarios.find((row) => row.id === 'optimised')!;
  const maximum = read.scenarios.find((row) => row.id === 'maximum')!;
  const beyond = read.scenarios.find((row) => row.id === 'beyond-limit')!;
  const selected = read.scenarios.find((row) => row.id === selectedScenarioId) ?? current;
  const lanesMax = Math.max(...read.circuits.map((row) => row.ceilingStph));
  const firstRate = Math.min(read.currentThroughputStph, ...read.bottleneckLadder.rungs.map((row) => row.ceilingStph));
  const x0 = Math.floor((firstRate * 0.8) / 5) * 5;
  // The scale ends far enough past the highest ceiling for that lane's value to sit right of its tick.
  const widestCeilingLabel = oneDecimal(lanesMax).length * CHAR_10_5 + 12;
  let x1 = Math.ceil(lanesMax * 1.015);
  while (GUTTER + ((W - GUTTER - RIGHT) * (lanesMax - x0)) / (x1 - x0) + widestCeilingLabel > W) x1 += 1;
  const X = (value: number) => GUTTER + ((W - GUTTER - RIGHT) * (value - x0)) / (x1 - x0);

  // ---- staircase steps: Current, then each rung in binding order.
  const steps: Step[] = [];
  if (!feedLimited) {
    steps.push({
      kind: 'here',
      x: read.currentThroughputStph,
      e: current.financials.ebitdaA,
      title: 'Current',
      pill: null,
      prefix: `${oneDecimal(read.currentThroughputStph)} stph · `,
      money: `${formatCurrency(current.financials.ebitdaA)}/day`,
      moneyTone: 'ink',
      tooltip: `Current · ${oneDecimal(read.currentThroughputStph)} stph · ${formatCurrency(current.financials.ebitdaA)}/day`,
    });
  }
  ladder.rungs.forEach((rung, index) => {
    const engineRung = read.bottleneckLadder.rungs[index]!;
    const limiting = feedLimited && index === 0;
    steps.push({
      kind: limiting ? 'lim' : rung.pill === 'Operating' ? 'op' : 'cap',
      x: engineRung.ceilingStph,
      e: engineRung.ebitdaA,
      title: rung.label,
      pill: rung.pill,
      prefix: `${rung.rateLine} · `,
      money: rung.money,
      moneyTone: limiting ? 'ink' : rung.moneyTone === 'bad' ? 'bad' : rung.moneyTone === 'ok' ? 'ok' : 'ink2',
      tooltip: [rung.label, rung.ceiling, rung.headroom, `${rung.ebitda} (${rung.ebitdaDelta})`, rung.stepGain, rung.caps].filter(Boolean).join(' · '),
    });
  });

  const drop = feedLimited ? null : {
    x: beyond.throughputStph,
    from: maximum.financials.ebitdaA,
    to: beyond.financials.ebitdaA,
    lead: `No capital ${formatCurrency(beyond.financials.ebitdaA)}/day`,
    rest: `Zn ${beyond.recoveries.znPct.toFixed(1)}% vs ${read.recoveryFloorZnPct.toFixed(1)}% floor · ${signedA(beyond.financials.ebitdaA - (maximum.financials.ebitdaA + read.step2.potential.ebitdaGainA))}/day`,
  };

  const eValues = [...steps.map((step) => step.e), ...(drop ? [drop.to] : [])];
  const eMin = Math.min(...eValues);
  const eMax = Math.max(...eValues);
  const eSpan = Math.max(eMax - eMin, 1);
  const e0 = eMin - eSpan * 0.12;
  const e1 = eMax + eSpan * 0.08;
  // Feed-limited reads have no recovery band, so the staircase takes the room and its rung labels spread out.
  const ladderHeight = feedLimited ? 250 : steps.length >= 4 ? 168 : 150;
  const EY = (value: number) => 24 + ((ladderHeight - 40) * (e1 - value)) / (e1 - e0);

  // ---- the staircase strokes, which no label may cross: each rung line (along
  // at the previous rung's EBITDA), each riser (up to the next), the drop line
  // below the first capital rung and the Current or feed-limit line to the lanes.
  const c = STROKE_CLEARANCE;
  const strokes: Box[] = [];
  for (let index = 1; index < steps.length; index += 1) {
    const xa = X(steps[index - 1]!.x);
    const xb = X(steps[index]!.x);
    const ya = EY(steps[index - 1]!.e);
    const yb = EY(steps[index]!.e);
    strokes.push({ left: Math.min(xa, xb), right: Math.max(xa, xb), top: ya - c, bottom: ya + c });
    strokes.push({ left: xb - c, right: xb + c, top: Math.min(ya, yb), bottom: Math.max(ya, yb) });
  }
  for (const step of steps) {
    if (step.kind === 'here' || step.kind === 'lim') {
      strokes.push({ left: X(step.x) - c, right: X(step.x) + c, top: EY(step.e), bottom: ladderHeight + 40 });
    }
  }
  if (drop) {
    strokes.push({ left: X(drop.x) - c, right: X(drop.x) + c, top: Math.min(EY(drop.from), EY(drop.to)), bottom: Math.max(EY(drop.from), EY(drop.to)) });
  }

  // ---- place step labels without collisions (greedy, deterministic).
  const obstacles: Box[] = [{ left: 0, right: 120, top: 0, bottom: 18 }, ...strokes];
  for (const step of steps) {
    const cx = X(step.x);
    const cy = EY(step.e);
    obstacles.push({ left: cx - 6, right: cx + 6, top: cy - 6, bottom: cy + 6 });
  }
  const labelBoxes: Box[] = [];
  const placed: { step: Step; x: number; y: number; anchor: 'start' | 'end' }[] = [];
  const dropLines = drop ? dropLabelLines(drop, X(drop.x), W) : null;
  if (drop && dropLines) {
    const nx = X(drop.x);
    const ny = EY(drop.to);
    const widest = Math.max(...dropLines.map((line) => line.length * CHAR_10_5));
    obstacles.push({ left: nx + 4, right: nx + 8 + widest, top: ny - 8, bottom: ny + 4 + (dropLines.length - 1) * 13 + 4 });
  }
  steps.forEach((step, index) => {
    const cx = X(step.x);
    const cy = EY(step.e);
    const titleWidth = step.title.length * CHAR_12_BOLD + (step.pill ? (step.pill.length + 3) * CHAR_10_CAPS : 0);
    const lineWidth = (step.prefix.length + step.money.length) * CHAR_10_5;
    const labelWidth = Math.max(titleWidth, lineWidth);
    const last = index === steps.length - 1;
    const candidates: { dx: number; dy: number; anchor: 'start' | 'end' }[] =
      step.kind === 'here'
        ? [{ dx: -10, dy: 4, anchor: 'end' }, { dx: -10, dy: 17, anchor: 'end' }, { dx: -10, dy: -18, anchor: 'end' }]
        : step.kind === 'lim'
          ? [{ dx: -10, dy: 4, anchor: 'end' }, { dx: -10, dy: -18, anchor: 'end' }, { dx: 10, dy: -18, anchor: 'start' }, { dx: 10, dy: 17, anchor: 'start' }]
          : last
            ? [{ dx: 10, dy: -4, anchor: 'start' }, { dx: 10, dy: -18, anchor: 'start' }, { dx: 10, dy: 17, anchor: 'start' }, { dx: -10, dy: -18, anchor: 'end' }, { dx: -10, dy: 4, anchor: 'end' }]
            : [{ dx: -10, dy: -18, anchor: 'end' }, { dx: 10, dy: -18, anchor: 'start' }, { dx: -10, dy: 17, anchor: 'end' }, { dx: 10, dy: 17, anchor: 'start' }, { dx: 10, dy: -4, anchor: 'start' }];
    const boxFor = (candidate: { dx: number; dy: number; anchor: 'start' | 'end' }, lift = 0): Box => {
      const x = cx + candidate.dx;
      const y = cy + candidate.dy - lift;
      return {
        left: candidate.anchor === 'end' ? x - labelWidth : x,
        right: candidate.anchor === 'end' ? x : x + labelWidth,
        top: y - 10,
        bottom: y + 16,
      };
    };
    const fits = (box: Box) => box.left >= 0 && box.right <= W && !obstacles.some((other) => overlaps(box, other));
    let chosen = candidates.find((candidate) => fits(boxFor(candidate)));
    let lift = 0;
    if (!chosen) {
      // Every side is taken: lift the preferred placement until it clears.
      chosen = candidates.find((candidate) => { const box = boxFor(candidate); return box.left >= 0 && box.right <= W; }) ?? candidates[0]!;
      while (lift < 120 && obstacles.some((other) => overlaps(boxFor(chosen!, lift), other))) lift += 13;
    }
    const box = boxFor(chosen, lift);
    obstacles.push(box);
    labelBoxes.push(box);
    placed.push({ step, x: cx + chosen.dx, y: cy + chosen.dy - lift, anchor: chosen.anchor });
  });
  const labelTop = Math.min(...labelBoxes.map((box) => box.top), ...steps.map((step) => EY(step.e) - 6), ...(drop ? [EY(drop.to) - 8] : []), 24);
  const shift = labelTop < 20 ? 20 - labelTop : 0;
  const ladderBottom = ladderHeight + shift;

  // ---- vertical layout below the ladder band.
  const laneTop = ladderBottom + 10;
  const lanesEnd = laneTop + read.circuits.length * LANE_HEIGHT;
  const recoveryTop = lanesEnd + 18;
  const height = feedLimited ? lanesEnd + 34 : recoveryTop + RECOVERY_HEIGHT + 30;
  const axisBottom = height - 24;

  const texts: AxisText[] = [];
  const lines: AxisLine[] = [];
  const rects: AxisRect[] = [];
  const circles: AxisCircle[] = [];
  const paths: AxisPath[] = [];
  const caps = (x: number, y: number, text: string, tone: AxisTone = 'ink3'): AxisText =>
    ({ x, y, anchor: 'start', size: 10.5, weight: 600, tone, spans: [{ text, tone }], caps: true });

  texts.push(caps(0, 14, 'EBITDA at rung'));
  texts.push(caps(0, laneTop - 4, 'Circuit ceilings'));
  if (!feedLimited) texts.push(caps(0, recoveryTop + 10, 'Recovery'));

  // Grid and axis ticks.
  for (let tick = Math.ceil(x0 / 5) * 5; tick <= x1; tick += 5) {
    lines.push({ x1: X(tick), x2: X(tick), y1: laneTop, y2: axisBottom, tone: 'ink3', width: tick % 10 === 0 ? 1 : 0.5, opacity: 0.35 });
    if (tick % 10 === 0) texts.push({ x: X(tick), y: height - 10, anchor: 'middle', size: 10, weight: 400, tone: 'ink3', spans: [{ text: String(tick), tone: 'ink3' }] });
  }
  texts.push({ x: GUTTER - 10, y: height - 10, anchor: 'end', size: 10, weight: 400, tone: 'ink3', spans: [{ text: 'Feed rate · stph', tone: 'ink3' }] });

  // Circuit ceilings as lanes on the same scale.
  read.circuits.forEach((circuit, index) => {
    const y = laneTop + index * LANE_HEIGHT + 4;
    const bottleneck = circuit.id === read.bottleneck;
    const tone: AxisTone = bottleneck ? (feedLimited ? 'bad' : 'warn') : 'ink3';
    const end = Math.min(X(circuit.ceilingStph), W - RIGHT);
    texts.push({ x: GUTTER - 10, y: y + 11, anchor: 'end', size: 11.5, weight: bottleneck ? 600 : 500, tone: bottleneck ? 'ink' : 'ink2', spans: [{ text: circuit.name, tone: bottleneck ? 'ink' : 'ink2' }] });
    rects.push({ x: GUTTER, y: y + 3, width: Math.max(0, end - GUTTER), height: 10, tone, opacity: bottleneck ? 0.85 : 0.22 });
    lines.push({ x1: end, x2: end, y1: y, y2: y + 16, tone: bottleneck ? tone : 'ink2', width: 2 });
    // The ceiling with its utilisation, right of the tick; near the right edge the
    // utilisation drops before the label moves inside the lane.
    const full = feedLimited ? oneDecimal(circuit.ceilingStph) : `${oneDecimal(circuit.ceilingStph)} · ${circuit.utilisationPct.toFixed(0)}%`;
    const fitsRight = (text: string) => end + 6 + text.length * CHAR_10_5 + 6 <= W;
    const label = fitsRight(full) ? full : oneDecimal(circuit.ceilingStph);
    const inside = !fitsRight(label);
    texts.push({ x: inside ? end - 6 : end + 6, y: y + 12, anchor: inside ? 'end' : 'start', size: 10.5, weight: 600, tone: bottleneck ? tone : 'ink2', spans: [{ text: label, tone: bottleneck ? tone : 'ink2' }] });
  });

  // Vertical guides: the optimised rate and the recovery floor, or the measured feed when D18 caps it.
  const guide = (value: number, tone: AxisTone, dash: string, label: string, labelY: number) => {
    if (value < x0 || value > x1) return;
    lines.push({ x1: X(value), x2: X(value), y1: laneTop - 6, y2: axisBottom, tone, width: 1.2, dash });
    texts.push({ x: X(value) + 4, y: labelY, anchor: 'start', size: 10, weight: 400, tone, spans: [{ text: label, tone }] });
  };
  if (feedLimited) {
    guide(read.measuredThroughputStph, 'ink3', '2 3', `${read.sourceTags.currentFeed} feed ${oneDecimal(read.measuredThroughputStph)}`, height - 28);
  } else {
    if (Math.abs(optimised.throughputStph - read.currentThroughputStph) > 0.05) {
      guide(optimised.throughputStph, 'ink3', '2 3', `optimised ${oneDecimal(optimised.throughputStph)}`, height - 40);
    }
    guide(read.recoveryFloorStph, 'bad', '4 3', `recovery floor ${oneDecimal(read.recoveryFloorStph)}`, height - 28);
  }

  // Staircase: along at the previous rung's EBITDA, then up to the next.
  const SY = (value: number) => EY(value) + shift;
  let d = `M${X(steps[0]!.x).toFixed(1)} ${SY(steps[0]!.e).toFixed(1)}`;
  for (let index = 1; index < steps.length; index += 1) {
    d += ` L${X(steps[index]!.x).toFixed(1)} ${SY(steps[index - 1]!.e).toFixed(1)} L${X(steps[index]!.x).toFixed(1)} ${SY(steps[index]!.e).toFixed(1)}`;
  }
  paths.push({ d, tone: 'ink2', width: 1.6 });
  for (let index = 1; index < steps.length; index += 1) {
    const gain = steps[index]!.e - steps[index - 1]!.e;
    lines.push({ x1: X(steps[index]!.x), x2: X(steps[index]!.x), y1: SY(steps[index - 1]!.e), y2: SY(steps[index]!.e), tone: gain >= 0 ? 'ok' : 'bad', width: 3 });
  }

  if (drop && dropLines) {
    const nx = X(drop.x);
    lines.push({ x1: nx, x2: nx, y1: SY(drop.from), y2: SY(drop.to), tone: 'bad', width: 1.4, dash: '3 3' });
    circles.push({ cx: nx, cy: SY(drop.to), r: 4, fill: 'surface', stroke: 'bad', strokeWidth: 1.8, title: `No capital · ${oneDecimal(drop.x)} stph · ${formatCurrency(drop.to)}/day` });
    dropLines.forEach((_, index) => {
      texts.push({
        x: nx + 8,
        y: SY(drop.to) + 4 + index * 13,
        anchor: 'start',
        size: 10.5,
        weight: 400,
        tone: 'bad',
        spans: dropLines.length === 1
          ? [{ text: drop.lead, tone: 'bad', weight: 600 }, { text: ` · ${drop.rest}`, tone: 'ink3' }]
          : index === 0 ? [{ text: drop.lead, tone: 'bad', weight: 600 }] : [{ text: drop.rest, tone: 'ink3' }],
      });
    });
  }

  placed.forEach(({ step, x, y, anchor }) => {
    const cx = X(step.x);
    const cy = SY(step.e);
    const here = step.kind === 'here';
    const limiting = step.kind === 'lim';
    const fill: AxisTone = here ? 'ink' : limiting ? 'bad' : step.kind === 'op' ? 'ok' : 'surface';
    const stroke: AxisTone = here ? 'ink' : limiting ? 'bad' : step.kind === 'op' ? 'ok' : 'warn';
    circles.push({ cx, cy, r: here || limiting ? 5.5 : 5, fill, stroke, strokeWidth: 2, title: step.tooltip });
    if (here || limiting) lines.push({ x1: cx, x2: cx, y1: cy, y2: axisBottom, tone: limiting ? 'bad' : 'ink', width: 1.6 });
    texts.push({
      x,
      y: y + shift,
      anchor,
      size: 12,
      weight: 600,
      tone: limiting ? 'bad' : 'ink',
      spans: [
        { text: step.title, tone: limiting ? 'bad' : 'ink' },
        ...(step.pill ? [{ text: ` · ${step.pill.toUpperCase()}`, tone: (step.kind === 'op' ? 'ok' : 'warn') as AxisTone, size: 10, weight: 600 as const }] : []),
      ],
    });
    texts.push({
      x,
      y: y + shift + 13,
      anchor,
      size: 10.5,
      weight: 400,
      tone: 'ink2',
      spans: [{ text: step.prefix, tone: 'ink2' }, { text: step.money, tone: step.moneyTone, weight: 600 }],
    });
  });

  // Recovery against the same feed axis, with the Zn floor and the scenario points.
  if (!feedLimited) {
    const points = read.recoverySeries.filter((point) => point.throughputStph >= x0 && point.throughputStph <= x1);
    const values = [...points.flatMap((point) => [point.znPct, point.pbPct]), read.recoveryFloorZnPct];
    const ry0 = Math.min(...values) - 1.1;
    const ry1 = Math.max(...values) + 1;
    const RY = (value: number) => recoveryTop + (RECOVERY_HEIGHT * (ry1 - value)) / (ry1 - ry0);
    for (let tick = Math.ceil(ry0 / 5) * 5; tick <= ry1; tick += 5) {
      texts.push({ x: GUTTER - 10, y: RY(tick) + 3, anchor: 'end', size: 10, weight: 400, tone: 'ink3', spans: [{ text: `${tick}%`, tone: 'ink3' }] });
      lines.push({ x1: GUTTER, x2: W - RIGHT, y1: RY(tick), y2: RY(tick), tone: 'ink3', width: 1, opacity: 0.25 });
    }
    lines.push({ x1: GUTTER, x2: W - RIGHT, y1: RY(read.recoveryFloorZnPct), y2: RY(read.recoveryFloorZnPct), tone: 'bad', width: 1.2, dash: '4 3' });
    texts.push({ x: W - RIGHT, y: RY(read.recoveryFloorZnPct) - 4, anchor: 'end', size: 10, weight: 400, tone: 'bad', spans: [{ text: `Zn floor ${read.recoveryFloorZnPct.toFixed(1)}%`, tone: 'bad' }] });
    const curve = (key: 'znPct' | 'pbPct') => points.map((point, index) => `${index ? 'L' : 'M'}${X(point.throughputStph).toFixed(1)} ${RY(point[key]).toFixed(1)}`).join('');
    paths.push({ d: curve('pbPct'), tone: 'info', width: 1.8 });
    paths.push({ d: curve('znPct'), tone: 'warn', width: 2 });
    const lastPoint = points[points.length - 1];
    if (lastPoint) {
      texts.push({ x: X(lastPoint.throughputStph) + 6, y: RY(lastPoint.pbPct) + 4, anchor: 'start', size: 10.5, weight: 600, tone: 'info', spans: [{ text: 'Pb', tone: 'info' }] });
      texts.push({ x: X(lastPoint.throughputStph) + 6, y: RY(lastPoint.znPct) + 4, anchor: 'start', size: 10.5, weight: 600, tone: 'warn', spans: [{ text: 'Zn', tone: 'warn' }] });
    }
    const marker = (scenario: typeof current, role: 'selected' | 'maximum' | 'context') => {
      const cx = X(scenario.throughputStph);
      const cy = RY(scenario.recoveries.znPct);
      const name = scenario.id === 'maximum' ? 'Maximum' : scenario.label;
      const title = `${scenario.label} · ${oneDecimal(scenario.throughputStph)} stph · Zn recovery ${scenario.recoveries.znPct.toFixed(1)}%`;
      if (role === 'selected') {
        circles.push({ cx, cy, r: 5, fill: 'accent', stroke: 'accent', strokeWidth: 1.8, title });
        texts.push({ x: cx - 8, y: cy - 11, anchor: 'end', size: 10.5, weight: 600, tone: 'accent', spans: [{ text: `${name} ${scenario.recoveries.znPct.toFixed(1)}%`, tone: 'accent' }] });
      } else if (role === 'maximum') {
        circles.push({ cx, cy, r: 3.5, fill: 'surface', stroke: 'ok', strokeWidth: 1.8, title });
        texts.push({ x: cx - 6, y: cy + 15, anchor: 'end', size: 10, weight: 400, tone: 'ok', spans: [{ text: `${name} ${scenario.recoveries.znPct.toFixed(1)}%`, tone: 'ok' }] });
      } else {
        circles.push({ cx, cy, r: 3.5, fill: 'surface', stroke: 'ink2', strokeWidth: 1.8, title });
      }
    };
    if (selected.id !== 'current') marker(current, 'context');
    if (selected.id !== 'maximum' && Math.abs(maximum.throughputStph - selected.throughputStph) > 1e-9) marker(maximum, 'maximum');
    marker(selected, 'selected');
  }

  return {
    width: W,
    height,
    ariaLabel: feedLimited
      ? `Bottleneck ladder on a feed-rate axis: feed limited to ${oneDecimal(read.currentThroughputStph)} stph by ${ladder.rungs[0]?.name ?? 'the binding circuit'}, capital rungs above`
      : `Bottleneck ladder on a feed-rate axis from ${oneDecimal(read.currentThroughputStph)} stph current, with circuit ceilings and Zn and Pb recovery against feed rate`,
    texts,
    lines,
    rects,
    circles,
    paths,
    stepLabelBoxes: labelBoxes.map((box) => ({ ...box, top: box.top + shift, bottom: box.bottom + shift })),
    stairStrokes: strokes.map((box) => ({ ...box, top: box.top + shift, bottom: box.bottom + shift })),
  };
}

/** One line when it fits beside the point, else the lead on its own line. */
function dropLabelLines(drop: { lead: string; rest: string }, x: number, width: number): readonly string[] {
  const single = `${drop.lead} · ${drop.rest}`;
  return x + 8 + single.length * CHAR_10_5 <= width ? [single] : [drop.lead, drop.rest];
}
